/**
 * WizardPdfImport — lets a paid user who landed on the manual wizard
 * skip the typing by uploading their LinkedIn PDF right here, behind the
 * SAME required consent gate the Dashboard uses (ConsentGate, purpose
 * `profile_analysis`). Users arriving from the Dashboard already granted
 * consent (passed via location.state) — we honour it and show a
 * "consent recorded" chip instead of re-asking.
 */
import React, { useRef, useState } from 'react';
import { CheckCircle2, FileUp, Loader2, Upload } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { linkedge } from '../../lib/api';
import { getErrorMessage } from '../../lib/errors';

export function WizardPdfImport({ initialConsent, onImported }) {
  const preConsented = !!initialConsent?.analysis;
  const [consent, setConsent] = useState(
    initialConsent || { analysis: false, cohort: false, marketing: false }
  );
  const [busy, setBusy] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const canUpload = !busy && consent.analysis;

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Please upload a PDF file');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('PDF too large (max 10MB)');
      return;
    }
    if (!consent.analysis) {
      toast.error('Please confirm the required consent to import your PDF.');
      return;
    }
    setBusy(true);
    const t = toast.loading('Uploading PDF...');
    try {
      const { job_id } = await linkedge.importPdf(file);
      toast.loading('Reading your profile with AI... ~20-40s', { id: t });
      const start = Date.now();
      const MAX_MS = 180_000;
      while (true) {
        await new Promise((r) => setTimeout(r, 3000));
        let job;
        try {
          job = await linkedge.getImportPdfJob(job_id);
        } catch (pollErr) {
          if (Date.now() - start > MAX_MS) throw pollErr;
          continue;
        }
        if (job.status === 'ready') {
          toast.success('Profile imported — review each step, then run your audit.', { id: t });
          onImported?.(job.profile, job.filename, consent);
          return;
        }
        if (job.status === 'failed') {
          throw new Error(job.error || 'Failed to parse PDF');
        }
        if (Date.now() - start > MAX_MS) {
          throw new Error('Parsing took too long. Try a smaller PDF.');
        }
      }
    } catch (err) {
      toast.error(getErrorMessage(err, 'Could not parse PDF'), { id: t });
    } finally {
      setBusy(false);
    }
  };

  const onFilePick = (e) => {
    handleFile(e.target.files?.[0]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFile(e.dataTransfer.files?.[0]);
  };

  return (
    <Card className="border-primary/30 bg-primary/[0.03] mb-6" data-testid="wizard-pdf-import-card">
      <CardContent className="p-5">
        <div className="flex items-start gap-3">
          <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary shrink-0">
            <FileUp size={18} />
          </span>
          <div className="min-w-0">
            <p className="font-semibold leading-tight">Have your LinkedIn PDF? Skip the typing.</p>
            <p className="text-sm text-muted-foreground mt-0.5">
              On LinkedIn: your profile &rarr; Resources &rarr; Save to PDF. Drop it here and we&rsquo;ll fill every step for you.
            </p>
          </div>
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept="application/pdf,.pdf"
          onChange={onFilePick}
          className="hidden"
          data-testid="wizard-pdf-file-input"
        />
        <div
          onClick={() => canUpload && fileInputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          className={`mt-4 rounded-2xl border-2 border-dashed transition-all p-5 text-center bg-card/70 ${
            dragOver ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/60'
          } ${busy ? 'opacity-60 cursor-progress' : canUpload ? 'cursor-pointer' : 'opacity-60 cursor-not-allowed'}`}
          data-testid="wizard-pdf-dropzone"
        >
          <div className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-secondary text-primary mb-2">
            {busy ? <Loader2 size={18} className="animate-spin" /> : <Upload size={18} />}
          </div>
          <p className="text-sm font-semibold">
            {busy ? 'Reading your PDF...' : 'Drop your LinkedIn PDF here'}
          </p>
          <p className="text-xs text-muted-foreground mt-0.5">or click to choose a file. Max 10MB.</p>
          {!consent.analysis && (
            <p className="text-[11.5px] text-primary mt-2 animate-pulse" data-testid="wizard-pdf-consent-hint">
              Check the required consent below to enable upload.
            </p>
          )}
          <Button type="button" variant="secondary" size="sm" className="mt-3" disabled={!canUpload} data-testid="wizard-pdf-choose-button">
            Choose PDF
          </Button>
        </div>

        {preConsented ? (
          <p
            className="mt-3 inline-flex items-center gap-1.5 text-[12px] text-emerald-700 dark:text-emerald-300"
            data-testid="wizard-pdf-consent-recorded"
          >
            <CheckCircle2 size={13} /> Consent recorded — you can upload your PDF above.
          </p>
        ) : (
          <label
            className={`mt-4 flex items-start gap-2.5 rounded-xl border px-3 py-2.5 text-[12px] leading-snug cursor-pointer transition-all ${
              consent.analysis
                ? 'border-emerald-500/40 bg-emerald-500/[0.06] text-emerald-700 dark:text-emerald-300'
                : 'border-primary/60 bg-primary/[0.04] text-foreground'
            }`}
            data-testid="wizard-pdf-consent-gate"
          >
            <input
              type="checkbox"
              checked={!!consent.analysis}
              onChange={(e) => setConsent({ ...consent, analysis: e.target.checked })}
              className="mt-0.5 h-4 w-4 shrink-0 rounded accent-primary"
              data-testid="wizard-pdf-consent-checkbox"
              aria-required="true"
            />
            <span>
              I agree to the{' '}
              <a href="/terms" target="_blank" rel="noopener noreferrer" className="underline underline-offset-2 hover:text-primary">Terms &amp; Conditions</a>{' '}
              &amp;{' '}
              <a href="/privacy" target="_blank" rel="noopener noreferrer" className="underline underline-offset-2 hover:text-primary">Privacy Policy</a>{' '}
              and confirm that this profile belongs to me or that I am authorised by the profile owner to submit it for analysis and audit.
            </span>
          </label>
        )}
      </CardContent>
    </Card>
  );
}

export default WizardPdfImport;
